const BASE_URL = require('./config');
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import BuildsPage from './pages/BuildsPage';
import ClientsPage from './pages/ClientsPage';
import PlanningPage from './pages/PlanningPage';
import ExpensesPage from './pages/ExpensesPage';
import PotentialPage from './pages/PotentialPage';

function TelegramLoginWidget({ onAuth }) {
  const containerRef = useRef(null);
  useEffect(() => {
    window.onTelegramAuth = (user) => { onAuth(user); };
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', 'mmachine_testcrm_auth_bot');
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    if (containerRef.current) { containerRef.current.innerHTML = ''; containerRef.current.appendChild(script); }
  }, [onAuth]);
  return <div ref={containerRef} className="flex justify-center min-h-[50px] mt-4"></div>;
}

function LoginPage({ onLogin }) {
  const [error, setError] = useState('');
  const handleTelegramResponse = async (user) => {
    try {
      const res = await fetch(BASE_URL + '/api/auth/telegram', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(user)
      });
      const data = await res.json();
      if (res.ok) { localStorage.setItem('tg_user', data.tg_id); onLogin(data.tg_id); } 
      else { setError(data.detail || 'Доступ запрещен'); }
    } catch (e) { setError('Ошибка связи с сервером'); }
  };
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl flex flex-col items-center">
        <h2 className="text-2xl font-black text-center text-slate-800 mb-2">Вход в CRM</h2>
        <p className="text-slate-500 text-sm text-center mb-6">Авторизуйтесь через Telegram</p>
        {error && <div className="bg-red-50 text-red-500 p-3 rounded mb-4 text-sm font-bold text-center w-full">{error}</div>}
        <TelegramLoginWidget onAuth={handleTelegramResponse} />
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(localStorage.getItem('tg_user'));
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => { setIsScrolled(window.scrollY > 10); setMenuOpen(false); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!user) { return <LoginPage onLogin={setUser} />; }
  const logout = () => { localStorage.removeItem('tg_user'); setUser(null); };

  const navLinkClass = ({ isActive }) =>
    `px-2 py-0.5 text-[11px] rounded-lg font-bold whitespace-nowrap ${isActive ? 'bg-[#4F75FF] text-white shadow-sm' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`;

  const mobileNavLinkClass = ({ isActive }) =>
    `block px-4 py-3 text-sm font-bold rounded-lg ${isActive ? 'bg-[#4F75FF] text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`;

  return (
    <Router>
      <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
        <nav className="bg-[#101828] text-white shadow-md sticky top-0 z-[60] border-b border-slate-800">
          <div className="max-w-[1600px] mx-auto px-4 h-full flex justify-between items-center w-full">
            {/* Десктоп навигация */}
            <div className="hidden md:flex items-center space-x-1">
              <NavLink to="/" className={navLinkClass}>Главная</NavLink>
              <NavLink to="/products" className={navLinkClass}>Склад товаров</NavLink>
              <NavLink to="/builds" className={navLinkClass}>Сборка ПК</NavLink>
              <NavLink to="/potential" className={navLinkClass}>Потенциальные</NavLink>
              <NavLink to="/clients" className={navLinkClass}>База клиентов</NavLink>
              <NavLink to="/planning" className={navLinkClass}>Планирование</NavLink>
              <NavLink to="/expenses" className={navLinkClass}>Траты</NavLink>
            </div>
            <div className="hidden md:flex items-center">
              <span className="text-slate-400 text-xs font-mono mr-4">ID: {user}</span>
              <button onClick={logout} className={"text-red-400 hover:text-red-300 font-bold bg-slate-800 rounded px-2 py-0.5 text-[11px]"}>Выйти</button>
            </div>
            {/* Мобильная навигация */}
            <div className="flex md:hidden items-center justify-between w-full">
              <span className="text-white font-bold text-sm">2M CRM</span>
              <div className="flex items-center space-x-2">
                <button onClick={logout} className="text-red-400 font-bold text-sm px-3 py-1.5 bg-slate-800 rounded">Выйти</button>
                <button onClick={() => setMenuOpen(!menuOpen)} className="text-white p-2 rounded bg-slate-800">
                  {menuOpen ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                  )}
                </button>
              </div>
            </div>
          </div>
          {/* Мобильное меню */}
          {menuOpen && (
            <div className="md:hidden bg-[#101828] border-t border-slate-800 px-4 py-3 space-y-1" onClick={() => setMenuOpen(false)}>
              <NavLink to="/" className={mobileNavLinkClass}>Главная</NavLink>
              <NavLink to="/products" className={mobileNavLinkClass}>Склад товаров</NavLink>
              <NavLink to="/builds" className={mobileNavLinkClass}>Сборка ПК</NavLink>
              <NavLink to="/potential" className={mobileNavLinkClass}>Потенциальные</NavLink>
              <NavLink to="/clients" className={mobileNavLinkClass}>База клиентов</NavLink>
              <NavLink to="/planning" className={mobileNavLinkClass}>Планирование</NavLink>
              <NavLink to="/expenses" className={mobileNavLinkClass}>Траты</NavLink>
            </div>
          )}
        </nav>
        <main className="pt-0">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/builds" element={<BuildsPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/planning" element={<PlanningPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/potential" element={<PotentialPage />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}
